import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";

const API_ROOT = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");

const getToday = () => new Date().toISOString().slice(0, 10);

function fmtDate(val) {
  if (!val) return "";
  const d = new Date(val);
  if (isNaN(d)) return String(val).slice(0, 10);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yy = d.getFullYear();
  return `${dd}/${mm}/${yy}`;
}

export default function StudentIndLeave() {
  const localUser = useMemo(() => {
    try { return JSON.parse(localStorage.getItem("user") || "null"); }
    catch { return null; }
  }, []);

  const moduleId = parseInt(localUser?.moduleId, 10);
  const deptId   = parseInt(localUser?.deptid,   10);
  const navigate = useNavigate();

  const [students,   setStudents]   = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [filter, setFilter] = useState({
    StudentID:   "",
    LeaveTypeID: "",
    FromDate:    "",
    ToDate:      getToday(),
  });
  const [result,  setResult]  = useState(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");

  useEffect(() => {
    if (moduleId !== 5) return;
    Promise.all([
      fetch(`${API_ROOT}/api/students/leave-students?moduleId=${moduleId}&deptId=${deptId}`).then(r => r.json()),
      fetch(`${API_ROOT}/api/students/all-leave-types?moduleId=${moduleId}`).then(r => r.json()),
    ])
      .then(([stuData, ltData]) => {
        if (Array.isArray(stuData)) setStudents(stuData);
        if (Array.isArray(ltData))  setLeaveTypes(ltData);
      })
      .catch(() => {});
  }, [moduleId, deptId]);

  const onChange = (e) => {
    const { name, value } = e.target;
    setFilter(prev => ({ ...prev, [name]: value }));
  };

  const onView = async (e) => {
    e.preventDefault();
    if (!filter.StudentID)   { alert("Please select a Student.");    return; }
    if (!filter.LeaveTypeID) { alert("Please select a Leave Type."); return; }
    setError("");
    setResult(null);
    setLoading(true);
    try {
      const params = new URLSearchParams({
        moduleId,
        studentId:   filter.StudentID,
        leaveTypeId: filter.LeaveTypeID,
        fromDate:    filter.FromDate || "2024-07-01",
        toDate:      filter.ToDate   || getToday(),
      });
      const res  = await fetch(`${API_ROOT}/api/students/ind-stu-leave?${params}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to load.");
      setResult(data);
    } catch (err) {
      setError(err.message || "Failed to load leave details.");
    } finally {
      setLoading(false);
    }
  };

  if (moduleId !== 5) {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger">Unauthorized access Or Session Timed out.</div>
      </div>
    );
  }

  return (
    <div className="container-fluid mt-3">
      <h3 className="ctitle">Individual Student Leave (From Date - To Date)</h3>

      <div className="mb-3 d-flex gap-2">
        <button type="button" className="btn btn-primary btn-sm" onClick={() => navigate("/student-leave-new")}>
          New Leave
        </button>
        <button type="button" className="btn btn-success btn-sm" onClick={() => navigate("/student-leave")}>
          View Leaves
        </button>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      <form onSubmit={onView}>
        <div className="row g-2 align-items-end mb-3">

          <div className="col-sm-4">
            <label className="form-label mb-1">Student</label>
            <select className="form-select" name="StudentID" value={filter.StudentID} onChange={onChange}>
              <option value="">Select Student</option>
              {students.map(s => (
                <option key={s.StudentID} value={s.StudentID}>
                  {s.StudentName}-{s.RollNo}
                </option>
              ))}
            </select>
          </div>

          <div className="col-sm-2">
            <label className="form-label mb-1">Leave Type</label>
            <select className="form-select" name="LeaveTypeID" value={filter.LeaveTypeID} onChange={onChange}>
              <option value="">Select</option>
              {leaveTypes.map(lt => (
                <option key={lt.LeaveTypeID} value={lt.LeaveTypeID}>{lt.LeaveType}</option>
              ))}
            </select>
          </div>

          <div className="col-sm-2">
            <label className="form-label mb-1">From Date</label>
            <input className="form-control" type="date" name="FromDate" value={filter.FromDate} onChange={onChange} required />
          </div>

          <div className="col-sm-2">
            <label className="form-label mb-1">To Date</label>
            <input className="form-control" type="date" name="ToDate" value={filter.ToDate} onChange={onChange} required />
          </div>

          <div className="col-sm-2">
            <label className="form-label mb-1">View</label>
            <button type="submit" className="btn btn-primary form-control" disabled={loading}>
              {loading ? "Loading..." : "View"}
            </button>
          </div>

        </div>
      </form>

      {result && (
        result.data.length === 0 ? (
          <div className="alert alert-info">No records found.</div>
        ) : (
          <div className="table-responsive">
            <table className="table table-bordered table-striped table-sm" style={{ fontSize: "13px" }}>
              <thead style={{ '--bs-table-bg': '#008080', color: 'white' }}>
                <tr>
                  <th>SNo</th>
                  <th>Student Name</th>
                  <th>Leave Type</th>
                  <th>From Date</th>
                  <th>To Date</th>
                  <th>Days</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {result.data.map((r, idx) => (
                  <tr key={idx}>
                    <td>{idx + 1}</td>
                    <td>{r.StudentName}</td>
                    <td>{r.LeaveType}</td>
                    <td>{fmtDate(r.FromDate)}</td>
                    <td>{fmtDate(r.ToDate)}</td>
                    <td>{r.NoOfDays}</td>
                    <td>{r.Status}</td>
                  </tr>
                ))}
                <tr>
                  <td colSpan={5} className="text-end"><strong>Total Days</strong></td>
                  <td><strong>{result.totalDays}</strong></td>
                  <td></td>
                </tr>
              </tbody>
            </table>
          </div>
        )
      )}
    </div>
  );
}
